import React from "react";
import { Ruler, Square, Box } from "lucide-react";
import { useWizard } from "@/lib/wizard";
import { useI18n } from "@/lib/i18n";

const NEON_CYAN  = "#00E5FF";
const NEON_GREEN = "#39FF14";
const NEON_AMBER = "#FFCC00";

function InputRow({ label, unit, value, onChange, testid, accent }) {
  return (
    <div className="grid grid-cols-12 items-center gap-3 border-2 border-[#1F1F1F] bg-black px-4 py-3 hover:border-[var(--neon-cyan)] transition-colors"
      data-testid={`${testid}-row`}>
      <div className="col-span-5 flex items-center gap-2">
        <span className="w-1.5 h-1.5" style={{ background: accent, boxShadow: `0 0 8px ${accent}` }}/>
        <span className="font-mono text-[11px] uppercase tracking-[0.22em] font-extrabold"
          style={{ color: accent }}>
          {label}
        </span>
      </div>
      <div className="col-span-5">
        <input
          type="number" step="0.01" min="0"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="w-full bg-black border-2 border-[#1F1F1F] focus:border-[var(--neon-cyan)] focus:outline-none text-white font-mono font-bold text-lg px-3 py-2 text-right tabular-nums"
          data-testid={testid}/>
      </div>
      <div className="col-span-2 font-mono text-sm font-bold text-white text-left">
        [{unit}]
      </div>
    </div>
  );
}

// Matte / elite result card — smaller font, NO glow, soft muted blue tones.
function ResultCard({ label, unit, value, accent, testid, icon: Icon, isLight }) {
  const valueColor = isLight ? "#0369A1" : "#7DD3FC";
  const labelColor = isLight ? "#64748B" : "#94A3B8";
  const unitColor  = isLight ? "#94A3B8" : "#64748B";
  return (
    <div className="border-2 bg-black p-4"
      style={{
        borderColor: isLight ? "#CBD5E1" : "#1F1F1F",
        boxShadow:   "none",
      }}
      data-testid={`${testid}-card`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: labelColor }}/>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] font-extrabold"
            style={{ color: labelColor }}>
            {label}
          </span>
        </div>
        <span className="font-mono text-[10px] font-bold" style={{ color: unitColor }}>[{unit}]</span>
      </div>
      <div className="mt-2 font-extrabold tracking-tight font-mono"
        data-testid={testid}
        style={{
          color: valueColor,
          fontSize: "1.4rem",
          lineHeight: 1.1,
          letterSpacing: "0.5px",
          opacity: 0.92,
          textShadow: "none",
        }}>
        {value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
    </div>
  );
}

export default function BoyutlarView({ conditions }) {
  const { t } = useI18n();
  const { dimensions, setDimensions } = useWizard();
  const { width, length, height } = dimensions;
  const area   = +(width * length).toFixed(4);
  const volume = +(width * length * height).toFixed(4);
  const wallArea = +(2 * (width + length) * height).toFixed(4);
  const isLight = conditions?._mode === "day";

  const update = (k, v) => setDimensions({ ...dimensions, [k]: v });

  // Theme-aware text colors for headings + formula block (matte elite palette).
  const sectionLabel = isLight ? "#475569" : "#FFFFFF";
  const formulaBg    = isLight ? "#F8FAFC" : "#000000";
  const formulaBorder = isLight ? "#CBD5E1" : "#1F1F1F";
  const formulaTitle = isLight ? "#1E40AF" : "#FFFFFF";
  const formulaText  = isLight ? "#334155" : "#FFFFFF";

  return (
    <section className="border-2 border-[var(--neon-cyan)] bg-black" data-testid="boyutlar-view">
      <header className="bg-[var(--neon-cyan)] text-black px-4 py-3 flex items-center gap-2">
        <Ruler className="w-5 h-5"/>
        <span className="font-mono text-sm uppercase tracking-[0.22em] font-extrabold">
          {t("boyut.header", "Dimensions · Geometric Parameters")}
        </span>
      </header>

      <div className="p-4 grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Inputs */}
        <div className="lg:col-span-6 space-y-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] font-extrabold"
            style={{ color: sectionLabel }}>
            ◆ {t("boyut.input", "Input · Dimension Entry")}
          </div>
          <InputRow label={t("boyut.width",  "Width")}  unit="m" value={width}
            onChange={(v) => update("width", v)}  testid="boyutlar-width"  accent={NEON_CYAN}/>
          <InputRow label={t("boyut.length", "Length")} unit="m" value={length}
            onChange={(v) => update("length", v)} testid="boyutlar-length" accent={NEON_CYAN}/>
          <InputRow label={t("boyut.height", "Height")} unit="m" value={height}
            onChange={(v) => update("height", v)} testid="boyutlar-height" accent={NEON_CYAN}/>

          <div className="mt-4 border-2 p-3"
            style={{ borderColor: formulaBorder, backgroundColor: formulaBg }}>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] font-extrabold mb-2"
              style={{ color: formulaTitle }}>
              ◆ {t("boyut.formula.title", "Formula Reference")}
            </div>
            <div className="font-mono text-[12px] font-bold space-y-1"
              style={{ color: formulaText }}>
              <div>{t("boyut.formula.area",   "Area  A = Width × Length")}</div>
              <div>{t("boyut.formula.volume", "Volume  V = Width × Length × Height")}</div>
              <div>{t("boyut.formula.wall",   "Wall Area = 2 × (W + L) × H")}</div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-6 space-y-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] font-extrabold"
            style={{ color: sectionLabel }}>
            ◆ {t("boyut.calculated", "Calculated · Results")}
          </div>
          <ResultCard label={t("boyut.area",   "Area")}      unit="m²" value={area}     accent={NEON_GREEN} testid="boyutlar-area"   icon={Square} isLight={isLight}/>
          <ResultCard label={t("boyut.volume", "Volume")}    unit="m³" value={volume}   accent={NEON_AMBER} testid="boyutlar-volume" icon={Box}    isLight={isLight}/>
          <ResultCard label={t("boyut.wall",   "Wall Area")} unit="m²" value={wallArea} accent={NEON_CYAN}  testid="boyutlar-wall"   icon={Square} isLight={isLight}/>
        </div>
      </div>
    </section>
  );
}
